<template>
  <div class="scraping-file">

    <!-- SESSION LIST -->
    <div v-if="view === 'sessions'">
      <div class="mb-3">
        <h5 class="mb-0">Scraping sur imports</h5>
      </div>

      <div v-if="sessionsLoading" class="text-center py-4">
        <b-spinner small /> Chargement...
      </div>
      <div v-else-if="!sessions.length" class="text-muted py-3">
        Aucun import. Importez un fichier CSV ou saisissez des lignes manuellement.
        <div class="mt-2 d-flex" style="gap:8px">
          <b-button variant="primary" size="sm" @click="openImportModal">
            <i class="fas fa-upload mr-1"></i> Importer un fichier
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="openManualModal(null)">
            <i class="fas fa-keyboard mr-1"></i> Saisie manuelle
          </b-button>
        </div>
      </div>
      <div v-else>
        <div class="d-flex justify-content-between mb-2">
          <b-button variant="outline-secondary" size="sm" @click="openAllView">
            <i class="fas fa-list mr-1"></i> Voir toutes les lignes
          </b-button>
          <div class="d-flex" style="gap:8px">
            <b-button variant="outline-primary" size="sm" @click="openManualModal(null)">
              <i class="fas fa-keyboard mr-1"></i> Saisie manuelle
            </b-button>
            <b-button variant="primary" size="sm" @click="openImportModal">
              <i class="fas fa-upload mr-1"></i> Importer un fichier
            </b-button>
          </div>
        </div>
      <table class="table table-sm table-hover">
        <thead class="thead-light">
          <tr>
            <th>Import</th>
            <th>Lignes</th>
            <th>Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in sessions" :key="s.uid">
            <td>
              {{ s.nom_fichier }}
              <span v-if="s.groupe" class="badge badge-info ml-1" style="font-size:0.75em">{{ s.groupe }}</span>
            </td>
            <td>{{ s.nb_lignes }}</td>
            <td>{{ formatDate(s.date) }}</td>
            <td class="text-right" style="white-space:nowrap">
              <b-button size="sm" variant="outline-primary" @click="selectSession(s)">
                <i class="fas fa-search-dollar mr-1"></i> Scraper
              </b-button>
              <b-button size="sm" variant="outline-secondary" class="ml-1" @click="openEditSessionModal(s)" title="Modifier le nom / groupe">
                <i class="fas fa-pencil-alt"></i>
              </b-button>
              <b-button size="sm" variant="outline-secondary" class="ml-1" @click="downloadSession(s)" title="Télécharger les résultats">
                <i class="fas fa-download"></i>
              </b-button>
              <b-button size="sm" variant="outline-danger" class="ml-1" @click="deleteSession(s)" title="Supprimer l'import">
                <i class="fas fa-trash"></i>
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <!-- ALL ROWS VIEW -->
    <div v-else-if="view === 'all'">
      <div class="d-flex align-items-center mb-3">
        <b-button size="sm" variant="outline-secondary" @click="view = 'sessions'">
          <i class="fas fa-arrow-left mr-1"></i> Imports
        </b-button>
        <h6 class="mb-0 ml-3">Toutes les lignes</h6>
      </div>
      <scraping-core :format="format" :data="data" :client="client" :datasource="scrapingDsAll" key="all">
        <template v-if="allDataKeys.length" #toolbar-right>
          <b-dropdown size="sm" variant="outline-secondary" no-caret toggle-class="d-flex align-items-center" right>
            <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes <span v-if="visibleAllDataColumns.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ visibleAllDataColumns.length }}</span></template>
            <b-dropdown-form style="min-width:280px;max-height:250px;overflow-y:auto">
              <div class="d-flex mb-2" style="gap:6px">
                <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="visibleAllDataColumns = [...allDataKeys]">Tout</b-button>
                <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="visibleAllDataColumns = []">Aucun</b-button>
              </div>
              <b-form-checkbox
                v-for="k in allDataKeys"
                :key="k"
                v-model="visibleAllDataColumns"
                :value="k"
                class="mb-1"
                style="font-size:0.85em"
              >{{ k }}</b-form-checkbox>
            </b-dropdown-form>
          </b-dropdown>
        </template>
      </scraping-core>
    </div>

    <!-- SCRAPING VIEW -->
    <div v-else-if="view === 'scraping' && currentSession">
      <div class="d-flex align-items-center mb-2 p-2 bg-light rounded">
        <b-button size="sm" variant="outline-secondary" @click="view = 'sessions'">
          <i class="fas fa-arrow-left mr-1"></i> Imports
        </b-button>
        <span class="ml-3 font-weight-bold">{{ currentSession.nom_fichier }}</span>
        <span v-if="currentSession.groupe" class="badge badge-info ml-2" style="font-size:0.8em">{{ currentSession.groupe }}</span>
        <b-button size="sm" variant="link" class="ml-1 p-0" @click="openEditSessionModal(currentSession)" title="Modifier le nom / groupe"><i class="fas fa-pencil-alt text-secondary"></i></b-button>
        <small class="text-muted ml-2">{{ currentSession.nb_lignes }} lignes · {{ formatDate(currentSession.date) }}</small>
        <div class="ml-auto d-flex align-items-center" style="gap:8px" v-if="!scrapingConfigViewActive">
          <b-button size="sm" variant="outline-primary" @click="openManualModal(currentSession)">
            <i class="fas fa-plus mr-1"></i> Ajouter des lignes
          </b-button>
          <b-dropdown size="sm" variant="outline-secondary" right>
            <template #button-content><i class="fas fa-download mr-1"></i> Télécharger</template>
            <b-dropdown-item @click="downloadOnlyValidated = false; downloadSession(currentSession)">
              <i class="fas fa-file-csv mr-1 text-secondary"></i> Tous les résultats
            </b-dropdown-item>
            <b-dropdown-item @click="downloadOnlyValidated = true; downloadSession(currentSession)">
              <i class="fas fa-check-circle mr-1 text-success"></i> Validés seulement
            </b-dropdown-item>
          </b-dropdown>
          <b-button size="sm" variant="outline-danger" @click="deleteSession(currentSession)">
            <i class="fas fa-trash mr-1"></i> Supprimer l'import
          </b-button>
        </div>
      </div>
      <scraping-core :format="format" :data="data" :client="client" :datasource="scrapingDs" :key="currentSession.uid" @config-view-change="scrapingConfigViewActive = $event">
        <template v-if="sessionDataKeys.length" #toolbar-right>
          <b-dropdown size="sm" variant="outline-secondary" no-caret toggle-class="d-flex align-items-center" right>
            <template #button-content><i class="fas fa-columns mr-1"></i>Colonnes <span v-if="visibleDataColumns.length" class="badge badge-primary ml-1" style="font-size:0.75em">{{ visibleDataColumns.length }}</span></template>
            <b-dropdown-form style="min-width:280px;max-height:250px;overflow-y:auto">
              <div class="d-flex mb-2" style="gap:6px">
                <b-button size="sm" variant="link" class="p-0 text-primary" style="font-size:0.8em" @click.stop="visibleDataColumns = [...sessionDataKeys]">Tout</b-button>
                <b-button size="sm" variant="link" class="p-0 text-secondary" style="font-size:0.8em" @click.stop="visibleDataColumns = []">Aucun</b-button>
              </div>
              <b-form-checkbox
                v-for="k in sessionDataKeys"
                :key="k"
                v-model="visibleDataColumns"
                :value="k"
                class="mb-1"
                style="font-size:0.85em"
              >{{ k }}</b-form-checkbox>
            </b-dropdown-form>
          </b-dropdown>
        </template>
      </scraping-core>
    </div>

    <!-- EDIT SESSION MODAL -->
    <b-modal v-model="editSessionModal" title="Modifier l'import" size="sm" hide-footer>
      <div class="form-group">
        <label class="font-weight-bold">Nom</label>
        <input v-model="editSessionNom" type="text" class="form-control form-control-sm" />
      </div>
      <div class="form-group">
        <label class="font-weight-bold">Groupe <small class="text-muted font-weight-normal">(optionnel)</small></label>
        <input v-model="editSessionGroupe" type="text" class="form-control form-control-sm" placeholder="ex: high-tech, vêtements…" :list="'edit-groupes-list'" autocomplete="off" />
        <datalist :id="'edit-groupes-list'">
          <option v-for="g in groupesList" :key="g" :value="g" />
        </datalist>
      </div>
      <div class="d-flex justify-content-end" style="gap:8px">
        <b-button size="sm" variant="outline-secondary" @click="editSessionModal = false">Annuler</b-button>
        <b-button size="sm" variant="primary" :disabled="editSessionSaving" @click="saveEditSession">
          <b-spinner v-if="editSessionSaving" small /> Sauvegarder
        </b-button>
      </div>
    </b-modal>

    <!-- MANUAL ENTRY MODAL -->
    <b-modal
      v-model="manualModal"
      :title="manualTargetSession ? 'Ajouter des lignes à : ' + manualTargetSession.nom_fichier : 'Saisie manuelle'"
      size="lg"
      hide-footer
    >
      <!-- Nom de l'import (nouvel import uniquement) -->
      <div v-if="!manualTargetSession" class="form-group">
        <label>Nom de l'import</label>
        <input v-model="manualSessionName" type="text" class="form-control form-control-sm" placeholder="Nom de l'import…" />
      </div>
      <div v-if="!manualTargetSession" class="form-group">
        <label>Groupe <small class="text-muted">(optionnel)</small></label>
        <input v-model="manualGroupe" type="text" class="form-control form-control-sm" placeholder="ex: high-tech, vêtements…" :list="'manual-groupes-list'" autocomplete="off" />
        <datalist :id="'manual-groupes-list'">
          <option v-for="g in groupesList" :key="g" :value="g" />
        </datalist>
      </div>

      <!-- ÉTAPE 1 : saisie + mapping -->
      <template v-if="manualStep === 'input'">
        <div class="form-group">
          <label><strong>Données</strong></label>
          <p class="text-muted small mb-1">
            Tapez vos données ou collez-les depuis Excel / Google Sheets.<br>
            Une ligne par produit, colonnes séparées par une tabulation ou un point-virgule.
          </p>
          <b-form-textarea v-model="manualText" rows="7" placeholder="Entrez ou collez vos données ici…" />
        </div>

        <template v-if="manualDetectedHeaders.length">
          <p class="mb-2 text-muted small">{{ Math.max(0, manualText.trim().split('\n').filter(l => l.trim()).length - 1) }} ligne(s) détectée(s) — Indiquez quelle colonne correspond à quoi :</p>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Clé externe <span class="font-weight-normal text-muted">(identifiant unique)</span></label>
              <select v-model="manualMapCle" class="form-control form-control-sm">
                <option value="">— Aucune —</option>
                <option v-for="(h, i) in manualDetectedHeaders" :key="i" :value="i">{{ h }}</option>
              </select>
            </div>
            <div class="form-group col-md-6">
              <label class="small font-weight-bold">Nom / libellé</label>
              <select v-model="manualMapNom" class="form-control form-control-sm">
                <option value="">— Aucune —</option>
                <option v-for="(h, i) in manualDetectedHeaders" :key="i" :value="i">{{ h }}</option>
              </select>
            </div>
          </div>
          <!-- Mapping des colonnes dans data -->
          <div class="mt-2 mb-2">
            <p class="small font-weight-bold mb-1">
              <i class="fas fa-exchange-alt mr-1 text-muted"></i> Renommer les colonnes dans <code>data</code>
              <span class="font-weight-normal text-muted">(optionnel)</span>
            </p>
            <div class="d-flex align-items-center mb-1" style="gap:6px">
              <b-button size="sm" variant="outline-primary" @click="toggleAllManualColumns(true)"><i class="fas fa-check-square mr-1"></i>Tout cocher</b-button>
              <b-button size="sm" variant="outline-secondary" @click="toggleAllManualColumns(false)"><i class="far fa-square mr-1"></i>Tout décocher</b-button>
            </div>
            <datalist id="scraper-cols-datalist-manual">
              <option v-for="col in allScraperColumnSuggestions" :key="col" :value="col" />
            </datalist>
            <div style="max-height:160px;overflow-y:auto;border:1px solid #dee2e6;border-radius:4px">
              <table class="table table-sm mb-0" style="font-size:0.82em">
                <thead class="thead-light" style="position:sticky;top:0;background:#f8f9fa">
                  <tr>
                    <th style="width:28px;text-align:center" title="Inclure dans data"><i class="fas fa-check-square"></i></th>
                    <th style="width:45%">Colonne du fichier</th>
                    <th>&rarr; Nom dans data</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="h in manualDetectedHeaders" :key="h">
                    <td class="text-center align-middle">
                      <input type="checkbox" v-model="manualColumnIncluded[h]" />
                    </td>
                    <td class="align-middle" style="white-space:nowrap"
                      :style="!manualColumnIncluded[h] ? 'text-decoration:line-through;opacity:0.4' : 'color:#6c757d'">{{ h }}</td>
                    <td :style="!manualColumnIncluded[h] ? 'opacity:0.3;pointer-events:none' : ''">
                      <input
                        :value="manualColumnMapping[h]"
                        @input="$set(manualColumnMapping, h, $event.target.value)"
                        type="text"
                        class="form-control form-control-sm"
                        :placeholder="h"
                        :disabled="!manualColumnIncluded[h]"
                        list="scraper-cols-datalist-manual"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="manualMissingScraperColumns.length" class="mt-2 p-2 rounded" style="background:#fff3cd;border:1px solid #ffc107;font-size:0.8em">
              <strong><i class="fas fa-exclamation-triangle mr-1 text-warning"></i>Colonnes manquantes :</strong>
              <span v-for="col in manualMissingScraperColumns" :key="col" class="ml-2">
                <span class="badge badge-warning">{{ col }}</span>
                <small class="text-muted ml-1">({{ (scraperColumnMap[col] || []).map(c => c.domain).join(', ') }})</small>
              </span>
            </div>
          </div>

          <div class="form-check mt-2">
            <input class="form-check-input" type="checkbox" v-model="manualSkipDuplicates" id="manualSkipDup" :disabled="manualMapCle === ''" />
            <label class="form-check-label small" for="manualSkipDup">
              Éviter les doublons (par clé externe, tous imports confondus)
            </label>
          </div>
        </template>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="outline-secondary" @click="manualModal = false" class="mr-2">Annuler</b-button>
          <b-button variant="primary" :disabled="!manualText.trim() || manualMapCle === '' || manualMapNom === ''" @click="buildManualPreview">
            <i class="fas fa-eye mr-1"></i> Prévisualiser
          </b-button>
        </div>
      </template>

      <!-- ÉTAPE 2 : prévisualisation -->
      <template v-if="manualStep === 'preview'">
        <p class="mb-2"><strong>{{ manualPreviewRows.length }} ligne(s) à importer</strong></p>
        <!-- Rappel du mapping -->
        <div class="d-flex mb-2" style="gap:16px">
          <span class="badge badge-light border">Clé externe → {{ manualDetectedHeaders[manualMapCle] }}</span>
          <span class="badge badge-light border">Nom → {{ manualDetectedHeaders[manualMapNom] }}</span>
        </div>
        <div style="max-height:400px;overflow-y:auto">
          <table class="table table-sm table-bordered table-hover">
            <thead class="thead-light">
              <tr>
                <th v-for="(h, i) in manualDetectedHeaders" :key="i"
                  :class="{'table-primary': i === parseInt(manualMapCle) || i === parseInt(manualMapNom)}">
                  {{ h }}
                  <small v-if="i === parseInt(manualMapCle)" class="ml-1 text-primary">→ Clé</small>
                  <small v-if="i === parseInt(manualMapNom)" class="ml-1 text-primary">→ Nom</small>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in manualPreviewRows" :key="i">
                <td v-for="(val, j) in row._raw" :key="j"
                  :class="{'font-weight-bold': j === parseInt(manualMapCle) || j === parseInt(manualMapNom)}">
                  {{ val }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="manualError" class="alert alert-danger py-2 mt-2">{{ manualError }}</div>
        <div v-if="manualSaving" class="text-center mt-2 text-muted">
          <b-spinner small class="mr-1" /> Enregistrement… {{ manualDone }}/{{ manualTotal }}
        </div>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="outline-secondary" @click="manualStep = 'input'" class="mr-2" :disabled="manualSaving">
            <i class="fas fa-arrow-left mr-1"></i> Modifier
          </b-button>
          <b-button variant="primary" :disabled="manualSaving || !manualPreviewRows.length" @click="doManualEntry">
            <b-spinner v-if="manualSaving" small class="mr-1" /> Importer
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- DATA VIEWER MODAL -->
    <b-modal v-model="dataModal" title="Données de la ligne" size="lg" ok-only ok-title="Fermer">
      <div v-if="dataModalRow">
        <div class="mb-2">
          <span class="badge badge-secondary mr-1">Clé externe : {{ dataModalRow.cle_externe }}</span>
          <span class="badge badge-secondary">Nom : {{ dataModalRow.nom }}</span>
        </div>
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr><th style="width:40%">Colonne</th><th>Valeur</th></tr>
          </thead>
          <tbody>
            <tr v-for="(val, key) in parsedDataModalData" :key="key">
              <td class="font-weight-bold">{{ key }}</td>
              <td>{{ val }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!Object.keys(parsedDataModalData).length" class="text-muted">Aucune donnée.</div>
      </div>
    </b-modal>

    <!-- IMPORT MODAL -->
    <b-modal
      v-model="importModal"
      title="Importer un fichier CSV"
      size="lg"
      :ok-title="importing ? 'Import en cours…' : 'Importer'"
      :ok-disabled="importing || !csvRows.length"
      cancel-title="Annuler"
      @ok.prevent="doImport"
    >
      <!-- Step 1: file picker -->
      <div v-if="!csvRows.length">
        <p class="text-muted">Formats acceptés : CSV (<code>;</code> ou <code>,</code>) ou Excel (<code>.xlsx</code>, <code>.xls</code>).</p>
        <div class="form-group">
          <label>Fichier</label>
          <input type="file" accept=".csv,.txt,.xlsx,.xls" class="form-control-file" @change="onFileChange" />
        </div>
        <div v-if="parseError" class="alert alert-danger py-2">{{ parseError }}</div>
      </div>

      <!-- Step 2: mapping + options -->
      <div v-else>
        <p>
          <strong>{{ csvFile.name }}</strong> —
          <span class="text-success">{{ csvRows.length }} lignes</span>,
          {{ csvHeaders.length }} colonnes détectées.
        </p>

        <div class="form-group">
          <label>Nom de l'import</label>
          <input v-model="sessionName" type="text" class="form-control form-control-sm" placeholder="Nom de l'import..." />
        </div>

        <div class="form-group">
          <label>Groupe <small class="text-muted">(optionnel)</small></label>
          <input v-model="sessionGroupe" type="text" class="form-control form-control-sm" placeholder="ex: high-tech, vêtements..." :list="'import-groupes-list'" autocomplete="off" />
          <datalist :id="'import-groupes-list'">
            <option v-for="g in groupesList" :key="g" :value="g" />
          </datalist>
        </div>

        <!-- Aperçu -->
        <div class="mb-3" style="overflow-x:auto;max-height:180px;overflow-y:auto;border:1px solid #dee2e6;border-radius:4px">
          <table class="table table-sm table-bordered mb-0" style="font-size:0.8em;white-space:nowrap">
            <thead class="thead-light" style="position:sticky;top:0">
              <tr>
                <th v-for="h in csvHeaders" :key="h">{{ h }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in csvRows.slice(0, 5)" :key="idx">
                <td v-for="h in csvHeaders" :key="h">{{ row[h] }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label>Colonne clé externe <small class="text-muted">(identifiant unique, pour dédoublonnage)</small></label>
            <select v-model="mapCleExterne" class="form-control form-control-sm">
              <option value="">— Aucune —</option>
              <option v-for="h in csvHeaders" :key="h" :value="h">{{ h }}</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label>Colonne nom / libellé</label>
            <select v-model="mapNom" class="form-control form-control-sm">
              <option value="">— Aucune —</option>
              <option v-for="h in csvHeaders" :key="h" :value="h">{{ h }}</option>
            </select>
          </div>
        </div>

        <!-- Mapping des colonnes dans data -->
        <div class="mt-1 mb-3">
          <p class="small font-weight-bold mb-1">
            <i class="fas fa-exchange-alt mr-1 text-muted"></i> Renommer les colonnes dans <code>data</code>
            <span class="font-weight-normal text-muted">(optionnel)</span>
          </p>
          <div class="d-flex align-items-center mb-1" style="gap:6px">
            <b-button size="sm" variant="outline-primary" @click="toggleAllColumns(true)"><i class="fas fa-check-square mr-1"></i>Tout cocher</b-button>
            <b-button size="sm" variant="outline-secondary" @click="toggleAllColumns(false)"><i class="far fa-square mr-1"></i>Tout décocher</b-button>
          </div>
          <datalist id="scraper-cols-datalist">
            <option v-for="col in allScraperColumnSuggestions" :key="col" :value="col" />
          </datalist>
          <div style="max-height:200px;overflow-y:auto;border:1px solid #dee2e6;border-radius:4px">
            <table class="table table-sm mb-0" style="font-size:0.82em">
              <thead class="thead-light" style="position:sticky;top:0;background:#f8f9fa">
                <tr>
                  <th style="width:28px;text-align:center" title="Inclure dans data"><i class="fas fa-check-square"></i></th>
                  <th style="width:45%">Colonne du fichier</th>
                  <th>→ Nom dans data</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in csvHeaders" :key="h">
                  <td class="text-center align-middle">
                    <input type="checkbox" v-model="columnIncluded[h]" />
                  </td>
                  <td class="align-middle" style="white-space:nowrap"
                    :style="!columnIncluded[h] ? 'text-decoration:line-through;opacity:0.4' : 'color:#6c757d'">{{ h }}</td>
                  <td :style="!columnIncluded[h] ? 'opacity:0.3;pointer-events:none' : ''">
                    <input
                      :value="columnMapping[h]"
                      @input="$set(columnMapping, h, $event.target.value)"
                      type="text"
                      class="form-control form-control-sm"
                      :placeholder="h"
                      :disabled="!columnIncluded[h]"
                      list="scraper-cols-datalist"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="csvMissingScraperColumns.length" class="mt-2 p-2 rounded" style="background:#fff3cd;border:1px solid #ffc107;font-size:0.8em">
            <strong><i class="fas fa-exclamation-triangle mr-1 text-warning"></i>Colonnes manquantes :</strong>
            <span v-for="col in csvMissingScraperColumns" :key="col" class="ml-2">
              <span class="badge badge-warning">{{ col }}</span>
              <small class="text-muted ml-1">({{ (scraperColumnMap[col] || []).map(c => c.domain).join(', ') }})</small>
            </span>
          </div>
        </div>

        <div class="form-check mb-3">
          <input class="form-check-input" type="checkbox" v-model="skipDuplicates" id="skipDup" :disabled="!mapCleExterne" />
          <label class="form-check-label" for="skipDup">
            Ignorer les lignes dont la clé externe existe déjà en base
            <small v-if="!mapCleExterne" class="text-muted">(nécessite une colonne clé externe)</small>
          </label>
        </div>

        <div v-if="importing" class="text-center py-2">
          <b-spinner small class="mr-2" />
          Import en cours… {{ importDone }}/{{ importTotal }}
        </div>
        <div v-if="importError" class="alert alert-danger py-2">{{ importError }}</div>

        <b-button size="sm" variant="outline-secondary" @click="resetImport">
          <i class="fas fa-redo mr-1"></i> Choisir un autre fichier
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

// ── CSV parser via PapaParse ──────────────────────────────────────────────────
function parseCSV(text) {
  const result = Papa.parse(text.trim(), {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: false,
  });
  const headers = result.meta.fields || [];
  const rows = result.data;
  console.log('[parseCSV] delimiter detected:', result.meta.delimiter);
  console.log('[parseCSV] headers:', headers);
  console.log('[parseCSV] rows count:', rows.length);
  console.log('[parseCSV] first row:', rows[0]);
  if (result.errors.length) console.warn('[parseCSV] errors:', result.errors);
  return { headers, rows };
}

function genUid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}
// ─────────────────────────────────────────────────────────────────────────────

export default {
  builder_info: 'Scraping sur fichier importé : import CSV, gestion des sessions import_session/import_produit, lancement scraping, téléchargement des résultats.',
  props: ['format', 'data', 'client'],

  data() {
    return {
      view: 'sessions',        // 'sessions' | 'scraping' | 'all'
      sessions: [],
      sessionsLoading: false,
      scrapingFileConfigs: [],  // scrapers de type scraping_file
      currentSession: null,
      allSessionFilter: '',     // uid de session pour filtrer la vue 'all'

      // Import modal
      importModal: false,
      csvFile: null,
      csvHeaders: [],
      csvRows: [],
      mapCleExterne: '',
      mapNom: '',
      sessionName: '',
      skipDuplicates: true,
      manualSkipDuplicates: true,
      downloadOnlyValidated: false,
      scrapingConfigViewActive: false,
      importing: false,
      importDone: 0,
      importTotal: 0,
      importError: '',
      parseError: '',
      columnMapping: {},
      columnIncluded: {},
      sessionGroupe: '',

      // Manual entry modal
      manualModal: false,
      manualTargetSession: null,
      manualSessionName: '',
      manualGroupe: '',
      manualStep: 'input',      // 'input' | 'preview'
      manualText: '',
      manualMapCle: '',
      manualMapNom: '',
      manualColumnMapping: {},
      manualColumnIncluded: {},
      manualPreviewRows: [],
      manualSaving: false,
      manualDone: 0,
      manualTotal: 0,
      manualError: '',

      // Data viewer modal
      dataModal: false,
      dataModalRow: null,

      // Colonnes détectées dans import_produit.data pour la session courante
      sessionDataKeys: [],
      // Colonnes détectées dans import_produit.data pour toutes les sessions
      allDataKeys: [],
      // Colonnes data visibles dans le tableau principal (session)
      visibleDataColumns: [],
      // Colonnes data visibles dans le tableau principal (toutes les lignes)
      visibleAllDataColumns: [],

      groupesList: [],

      // Edit session modal
      editSessionModal: false,
      editSessionTarget: null,
      editSessionNom: '',
      editSessionGroupe: '',
      editSessionSaving: false,
    };
  },

  watch: {
    manualDetectedHeaders(headers) {
      const cm = {};
      const ci = {};
      headers.forEach(h => {
        cm[h] = (this.manualColumnMapping[h] || '').trim() || h;
        ci[h] = this.manualColumnIncluded[h] !== false;
      });
      this.manualColumnMapping = cm;
      this.manualColumnIncluded = ci;
    },
  },

  computed: {
    scraperColumnMap() {
      const map = {};
      for (const cfg of this.scrapingFileConfigs) {
        for (const col of this.extractScraperColumns(cfg)) {
          if (!map[col]) map[col] = [];
          map[col].push(cfg);
        }
      }
      return map;
    },

    allScraperColumnSuggestions() {
      return Object.keys(this.scraperColumnMap).sort();
    },

    csvMissingScraperColumns() {
      if (!this.csvHeaders.length) return [];
      const provided = new Set(
        this.csvHeaders
          .filter(h => this.columnIncluded[h] !== false)
          .map(h => (this.columnMapping[h] || '').trim() || h)
      );
      return this.allScraperColumnSuggestions.filter(col => !provided.has(col));
    },

    manualMissingScraperColumns() {
      if (!this.manualDetectedHeaders.length) return [];
      const provided = new Set(
        this.manualDetectedHeaders
          .filter(h => this.manualColumnIncluded[h] !== false)
          .map(h => (this.manualColumnMapping[h] || '').trim() || h)
      );
      return this.allScraperColumnSuggestions.filter(col => !provided.has(col));
    },

    scrapingDsAll() {
      const filterUid = this.allSessionFilter;
      const safeUid = filterUid ? filterUid.replace(/'/g, "''") : null;
      const baseWhere = safeUid
        ? `import_produit.import_uid = '${safeUid}'`
        : null;
      return {
        table: 'import_produit',
        baseWhere,
        scrapingJoinId: 'import_produit.id',
        selectFields: 'import_produit.id, import_produit.import_uid, import_produit.cle_externe, import_produit.nom, import_produit.data, import_produit.date, import_session.nom_fichier, import_produit.groupe',
        selectFieldsJoined: 'import_produit.id, import_produit.import_uid, import_produit.cle_externe, import_produit.nom, import_produit.data, import_produit.date, import_session.nom_fichier, import_produit.groupe',
        extraJoin: 'LEFT JOIN import_session ON import_session.uid = import_produit.import_uid',
        listingColumns: [
          { key: 'nom_fichier', label: 'Import' },
          { key: 'cle_externe', label: 'Clé externe' },
          { key: 'nom',         label: 'Nom' },
          { key: 'groupe',      label: 'Groupe', render: (v) => v || '—' },
          ...this.visibleAllDataColumns.map(k => ({ key: `data.${k}`, label: k, fromData: k })),
        ],
        conditionTestSelect: 'import_produit.id, import_produit.cle_externe, import_produit.nom, import_produit.data, import_session.nom_fichier',
        scrapTestSelect: 'import_produit.id, import_produit.cle_externe, import_produit.nom, import_produit.data, import_session.nom_fichier, import_produit.groupe',
        productColumnsTable: 'import_produit',
        defaultProductColumns: ['cle_externe', 'nom'],
        nomSqlTransform: false,
        jsonColumns: [],
        configType: 'scraping_file',
        showNomColumn: false,
        showCategoriesColumn: false,
        showCaracteristiques: false,
        showDataKeys: true,
        showVeilleTarifaire: false,
        categoriesSelect: null,
        categoriesIsJson: false,
        getNom: (p) => p.nom || p.cle_externe || String(p.id),
        getCleProduir: (p) => p.cle_externe || '',
        qbMainFilters: [
          { id: 'import_produit.id',          label: 'id',          type: 'integer' },
          { id: 'import_produit.cle_externe', label: 'Clé externe', type: 'string'  },
          { id: 'import_produit.nom',         label: 'Nom',         type: 'string'  },
          { id: 'import_session.nom_fichier', label: 'Import',      type: 'string'  },
          ...this.allDataKeys.map(k => ({ id: `data.${k}`, label: k, type: 'string' })),
        ],
        qbTestFilters: [
          { id: 'nom',         label: 'Nom',         type: 'string' },
          { id: 'import_produit.cle_externe', label: 'Clé externe', type: 'string' },
          { id: 'import_session.nom_fichier', label: 'Import',      type: 'string' },
          ...this.allDataKeys.map(k => ({ id: `data.${k}`, label: k, type: 'string' })),
        ],
        qbFields: [
          { value: 'import_produit.nom',         label: 'Nom',         type: 'text' },
          { value: 'import_produit.cle_externe', label: 'Clé externe', type: 'text' },
          { value: 'nom_fichier',                label: 'Import',      type: 'text' },
          ...this.allDataKeys.map(k => ({ value: `data.${k}`, label: k, type: 'text' })),
        ],
        identificationOptions: [],
        rowActions: [
          { key: 'view-data', icon: 'fas fa-table', label: 'Voir les données', variant: 'outline-info', onClick: (row) => this.openDataModal(row) },
        ],
      };
    },

    parsedDataModalData() {
      if (!this.dataModalRow) return {};
      try {
        return JSON.parse(this.dataModalRow.data || '{}');
      } catch (e) { return {}; }
    },

    manualDetectedHeaders() {
      const text = this.manualText.trim();
      if (!text) return [];
      const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
      if (!lines.length) return [];
      const sep = lines[0].includes('\t') ? '\t' : ';';
      // La première ligne est l'en-tête
      return lines[0].split(sep).map(c => c.trim());
    },

    scrapingDs() {
      if (!this.currentSession) return {};
      const safeUid = this.currentSession.uid.replace(/'/g, "''");
      return {
        table: 'import_produit',
        baseWhere: `import_uid = '${safeUid}'`,
        scrapingJoinId: 'import_produit.id',
        selectFields: 'id, import_uid, cle_externe, nom, data, date, groupe',
        selectFieldsJoined: 'import_produit.id, import_produit.import_uid, import_produit.cle_externe, import_produit.nom, import_produit.data, import_produit.date, import_produit.groupe',
        listingColumns: [
          { key: 'cle_externe', label: 'Clé externe' },
          { key: 'nom',         label: 'Nom' },
          ...this.visibleDataColumns.map(k => ({ key: `data.${k}`, label: k, fromData: k })),
        ],
        conditionTestSelect: 'id, cle_externe, nom, data',
        conditionTestColumns: [
          { key: 'cle_externe', label: 'Clé externe' },
          { key: 'nom',         label: 'Nom' },
        ],
        scrapTestSelect: 'id, cle_externe, nom, data, groupe',
        productColumnsTable: 'import_produit',
        defaultProductColumns: ['cle_externe', 'nom'],
        noMarquesList: true,
        nomSqlTransform: false,
        jsonColumns: [],
        configType: 'scraping_file',
        showNomColumn: false,
        showCategoriesColumn: false,
        showCaracteristiques: false,
        showDataKeys: true,
        showVeilleTarifaire: false,
        fixedGroupe: this.currentSession.groupe || null,
        categoriesSelect: null,
        categoriesIsJson: false,
        getNom: (p) => p.nom || p.cle_externe || String(p.id),
        getCleProduir: (p) => p.cle_externe || '',
        qbMainFilters: [
          { id: 'id',          label: 'id',          type: 'integer' },
          { id: 'cle_externe', label: 'Clé externe', type: 'string'  },
          { id: 'nom',         label: 'Nom',         type: 'string'  },
          ...this.sessionDataKeys.map(k => ({ id: `data.${k}`, label: k, type: 'string' })),
        ],
        qbTestFilters: [
          { id: 'nom',         label: 'Nom',         type: 'string' },
          { id: 'cle_externe', label: 'Clé externe', type: 'string' },
          ...this.sessionDataKeys.map(k => ({ id: `data.${k}`, label: k, type: 'string' })),
        ],
        qbFields: [
          { value: 'nom',         label: 'Nom',         type: 'text' },
          { value: 'cle_externe', label: 'Clé externe', type: 'text' },
          ...this.sessionDataKeys.map(k => ({ value: `data.${k}`, label: k, type: 'text' })),
        ],
        identificationOptions: [],
        rowActions: [
          { key: 'view-data', icon: 'fas fa-table', label: 'Voir les données', variant: 'outline-info', onClick: (row) => this.openDataModal(row) },
        ],
      };
    },
  },

  mounted() {
    this.loadSessions();
    this.loadScrapingFileConfigs();
  },

  methods: {
    normalizeColKey(s) {
      return (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_');
    },

    toggleAllManualColumns(val) {
      this.manualDetectedHeaders.forEach(h => this.$set(this.manualColumnIncluded, h, val));
    },

    toggleAllColumns(val) {
      this.csvHeaders.forEach(h => this.$set(this.columnIncluded, h, val));
    },

    extractScraperColumns(cfg) {
      const DIRECT_FIELDS = new Set(['nom', 'cle_externe']);
      const cols = new Set();
      if (cfg.before_code) {
        for (const m of [...cfg.before_code.matchAll(/_dataJson\['([^']+)'\]/g)]) {
          if (!DIRECT_FIELDS.has(m[1])) cols.add(m[1]);
        }
      }
      return [...cols];
    },

    async loadScrapingFileConfigs() {
      try {
        const rows = await this.database.get('select', 'scraping_config', {
          select: 'id, domain, identification, query, before_code, groupe',
          where: `type = 'scraping_file' AND active = 1`,
        });
        this.scrapingFileConfigs = rows || [];
        const groupesRows = await this.database.get('select', 'scraping_config', {
          select: 'DISTINCT groupe',
          where: `groupe IS NOT NULL AND groupe != ''`,
          end: 'ORDER BY groupe ASC',
        });
        this.groupesList = (groupesRows || []).map(r => r.groupe).filter(Boolean);
      } catch (e) {
        this.scrapingFileConfigs = [];
      }
    },

    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },

    async loadSessions() {
      this.sessionsLoading = true;
      try {
        const rows = await this.database.get('select', 'import_session', {
          select: '*',
          end: 'ORDER BY date DESC LIMIT 200',
        });
        this.sessions = rows || [];
      } finally {
        this.sessionsLoading = false;
      }
      // Charger un échantillon de clés data pour la vue toutes-sessions (une ligne par session pour couvrir toutes les colonnes)
      try {
        const keys = new Set();
        for (const session of (this.sessions || [])) {
          const safeUid = session.uid.replace(/'/g, "''");
          const sample = await this.database.get('select', 'import_produit', {
            select: 'data',
            where: `import_uid = '${safeUid}' AND data IS NOT NULL AND data != '' AND data != '{}'`,
            end: 'LIMIT 2',
          });
          for (const r of sample || []) {
            try { Object.keys(JSON.parse(r.data || '{}')).forEach(k => keys.add(k)); } catch (e) {}
          }
        }
        this.allDataKeys = [...keys];
      } catch (e) {
        this.allDataKeys = [];
      }
    },

    async selectSession(s) {
      this.sessionDataKeys = [];
      await this.loadSessionDataKeys(s);
      this.currentSession = s;
      this.view = 'scraping';
    },

    async loadSessionDataKeys(session) {
      try {
        const safeUid = session.uid.replace(/'/g, "''");
        const rows = await this.database.get('select', 'import_produit', {
          select: 'data',
          where: `import_uid = '${safeUid}' AND data IS NOT NULL AND data != '' AND data != '{}'`,
          end: 'LIMIT 5',
        });
        const keys = new Set();
        for (const r of rows || []) {
          try {
            const parsed = JSON.parse(r.data || '{}');
            Object.keys(parsed).forEach(k => keys.add(k));
          } catch (e) {}
        }
        this.sessionDataKeys = [...keys];
      } catch (e) {
        this.sessionDataKeys = [];
      }
    },

    openAllView() {
      this.allSessionFilter = '';
      this.view = 'all';
    },

    allFilterChanged() {
      // réinitialisation déclenchée par le changement de v-model, le computed se recalcule
    },

    openDataModal(row) {
      this.dataModalRow = row;
      this.dataModal = true;
    },

    openManualModal(session) {
      const newTarget = session || null;
      const sameTarget = (
        (!newTarget && !this.manualTargetSession) ||
        (newTarget && this.manualTargetSession && newTarget.uid === this.manualTargetSession.uid)
      );
      if (!sameTarget) {
        // Cible différente : reset complet
        this.manualTargetSession = newTarget;
        this.manualSessionName = '';
        this.manualStep = 'input';
        this.manualText = '';
        this.manualMapCle = '';
        this.manualMapNom = '';
        this.manualColumnMapping = {};
        this.manualColumnIncluded = {};
        this.manualPreviewRows = [];
        this.manualSkipDuplicates = true;
      }
      // Dans tous les cas, reset des états transitoires
      this.manualSaving = false;
      this.manualDone = 0;
      this.manualTotal = 0;
      this.manualError = '';
      this.manualModal = true;
    },

    buildManualPreview() {
      const text = this.manualText.trim();
      if (!text) return;
      const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
      const sep = lines[0].includes('\t') ? '\t' : ';';
      const cleIdx = this.manualMapCle !== '' ? parseInt(this.manualMapCle) : null;
      const nomIdx = this.manualMapNom !== '' ? parseInt(this.manualMapNom) : null;
      const nbCols = this.manualDetectedHeaders.length;
      // Sauter la première ligne (en-tête)
      this.manualPreviewRows = lines.slice(1)
        .map(l => {
          const parts = l.split(sep).map(c => c.trim());
          const raw = Array.from({length: nbCols}, (_, i) => parts[i] || '');
          return {
            _raw: raw,
            cle_externe: cleIdx !== null ? (parts[cleIdx] || '') : '',
            nom:         nomIdx !== null ? (parts[nomIdx] || '') : '',
          };
        })
        .filter(r => r.cle_externe || r.nom);
      this.manualError = '';
      this.manualStep = 'preview';
    },

    async doManualEntry() {
      const validRows = this.manualPreviewRows;
      if (!validRows.length) return;
      this.manualSaving = true;
      this.manualError = '';
      this.manualTotal = validRows.length;
      this.manualDone = 0;
      try {
        let targetSession = this.manualTargetSession;
        if (!targetSession) {
          const sessionUid = genUid();
          await this.database.get('insert into', 'import_session', {
            columns: ['uid', 'nom_fichier', 'colonne_cle', 'mapping', 'nb_lignes', 'groupe'],
            data: {
              uid: sessionUid,
              nom_fichier: this.manualSessionName || 'Saisie manuelle',
              colonne_cle: '',
              mapping: '{}',
              nb_lignes: validRows.length,
              groupe: this.manualGroupe && this.manualGroupe.trim() ? this.manualGroupe.trim() : null,
            },
          });
          await this.loadSessions();
          targetSession = this.sessions.find(s => s.uid === sessionUid);
        }
        const sessionUid = targetSession.uid;
        // Charger les clés existantes pour éviter les doublons (toutes sessions)
        let existingKeys = new Set();
        if (this.manualSkipDuplicates) {
          const existing = await this.database.get('select', 'import_produit', {
            select: 'cle_externe',
            where: `cle_externe IS NOT NULL AND cle_externe != ''`,
            end: 'LIMIT 100000',
          });
          existingKeys = new Set((existing || []).map(r => r.cle_externe).filter(Boolean));
        }
        for (const row of validRows) {
          const allData = {};
          // Sauter les doublons si option activée
          if (this.manualSkipDuplicates && row.cle_externe && existingKeys.has(row.cle_externe)) {
            this.manualTotal--;
            continue;
          }
          // Stocker toutes les colonnes brutes en appliquant le mapping
          if (row._raw && this.manualDetectedHeaders.length) {
            this.manualDetectedHeaders.forEach((h, i) => {
              if (this.manualColumnIncluded[h] === false) return;
              const mappedKey = (this.manualColumnMapping[h] || '').trim() || h;
              allData[mappedKey] = row._raw[i] || '';
            });
          } else {
            allData.cle_externe = row.cle_externe || '';
            allData.nom = row.nom || '';
          }
          await this.database.get('insert into', 'import_produit', {
            columns: ['import_uid', 'cle_externe', 'nom', 'data', 'groupe'],
            data: {
              import_uid: sessionUid,
              cle_externe: row.cle_externe || '',
              nom: row.nom || '',
              data: JSON.stringify(allData),
              groupe: this.manualGroupe && this.manualGroupe.trim() ? this.manualGroupe.trim() : null,
            },
          });
          this.manualDone++;
        }
        // Mettre à jour nb_lignes si ajout à session existante
        if (this.manualTargetSession) {
          const safeUid = sessionUid.replace(/'/g, "''");
          const countRes = await this.database.get('select', 'import_produit', {
            select: 'COUNT(*) AS total',
            where: `import_uid = '${safeUid}'`,
          });
          const newCount = countRes?.[0]?.total || 0;
          await this.database.get('update', 'import_session', {
            set: `nb_lignes = ${newCount}`,
            where: `uid = '${safeUid}'`,
          });
        }
        this.manualModal = false;
        this.manualTargetSession = null;  // force reset à la prochaine ouverture
        this.manualText = '';
        this.manualMapCle = '';
        this.manualMapNom = '';
        this.manualColumnMapping = {};
        this.manualColumnIncluded = {};
        this.manualPreviewRows = [];
        this.manualSessionName = '';
        await this.loadSessions();
        const newSession = this.sessions.find(s => s.uid === sessionUid);
        if (newSession) this.selectSession(newSession);
      } catch (e) {
        this.manualError = 'Erreur : ' + (e.message || String(e));
      } finally {
        this.manualSaving = false;
      }
    },

    openImportModal() {
      // Ne reset que les états transitoires (erreurs, progression) — les données du fichier sont conservées
      this.importing = false;
      this.importDone = 0;
      this.importTotal = 0;
      this.importError = '';
      this.parseError = '';
      this.importModal = true;
    },

    resetImport() {
      this.csvFile = null;
      this.csvHeaders = [];
      this.csvRows = [];
      this.mapCleExterne = '';
      this.mapNom = '';
      this.sessionName = '';
      this.sessionGroupe = '';
      this.skipDuplicates = true;
      this.importing = false;
      this.importDone = 0;
      this.importTotal = 0;
      this.importError = '';
      this.parseError = '';
      this.columnMapping = {};
      this.columnIncluded = {};
    },

    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.csvFile = file;
      this.parseError = '';
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          let headers, rows;
          if (isExcel) {
            const wb = XLSX.read(ev.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
            headers = data.length ? Object.keys(data[0]) : [];
            rows = data.map(r => {
              const obj = {};
              headers.forEach(h => { obj[h] = r[h] !== undefined ? String(r[h]) : ''; });
              return obj;
            });
          } else {
            ({ headers, rows } = parseCSV(ev.target.result));
          }
          if (!headers.length) { this.parseError = 'Fichier vide ou format non lisible.'; return; }
          this.csvHeaders = headers;
          this.csvRows = rows;
          // Auto-fill session name from filename
          this.sessionName = file.name.replace(/\.[^.]+$/, '');
          // Auto-detect sensible columns
          this.mapCleExterne = headers.find(h => /ref|ean|id|code|sku|cle|key/i.test(h)) || '';
          this.mapNom = headers.find(h => /^(nom|name|titre|title|libelle)$/i.test(h)) || '';
          const cm = {};
          const ci = {};
          headers.forEach(h => { cm[h] = h; ci[h] = true; });
          this.columnMapping = cm;
          this.columnIncluded = ci;
        } catch (err) {
          this.parseError = 'Erreur de lecture : ' + err.message;
        }
      };
      if (isExcel) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file, 'UTF-8');
      }
    },

    async doImport() {
      if (!this.csvRows.length || this.importing) return;
      this.importing = true;
      this.importError = '';
      try {
        const sessionUid = genUid();
        const mapping = {};
        this.csvHeaders.forEach(h => { mapping[h] = (this.columnMapping[h] || '').trim() || h; });

        const applyMapping = (r) => {
          const mapped = {};
          Object.keys(r).forEach(h => {
            if (this.columnIncluded[h] === false) return;
            mapped[mapping[h] || h] = r[h];
          });
          return mapped;
        };

        let rows = this.csvRows.map(r => ({
          import_uid: sessionUid,
          cle_externe: this.mapCleExterne ? (r[this.mapCleExterne] || '') : '',
          nom:         this.mapNom        ? (r[this.mapNom]        || '') : '',
          data:        JSON.stringify(applyMapping(r)),
          groupe:      this.sessionGroupe && this.sessionGroupe.trim() ? this.sessionGroupe.trim() : null,
        }));

        // Dédoublonnage par cle_externe
        if (this.skipDuplicates && this.mapCleExterne) {
          const allCles = [...new Set(rows.map(r => r.cle_externe).filter(Boolean))];
          if (allCles.length) {
            const clesStr = allCles.map(c => `'${c.replace(/'/g, "''")}'`).join(',');
            const existing = await this.database.get('select', 'import_produit', {
              select: 'cle_externe',
              where: `cle_externe IN (${clesStr})`,
            });
            const existingSet = new Set((existing || []).map(e => e.cle_externe));
            rows = rows.filter(r => !existingSet.has(r.cle_externe));
          }
        }

        if (!rows.length) {
          this.importError = 'Toutes les lignes existent déjà en base (doublons ignorés).';
          this.importing = false;
          return;
        }

        this.importTotal = rows.length;
        this.importDone = 0;

        // Créer la session
        await this.database.get('insert into', 'import_session', {
          columns: ['uid', 'nom_fichier', 'colonne_cle', 'mapping', 'nb_lignes', 'groupe'],
          data: {
            uid: sessionUid,
            nom_fichier: this.sessionName || this.csvFile.name,
            colonne_cle: this.mapCleExterne || '',
            mapping: JSON.stringify(mapping),
            nb_lignes: rows.length,
            groupe: this.sessionGroupe && this.sessionGroupe.trim() ? this.sessionGroupe.trim() : null,
          },
        });

        // Insérer les lignes par batchs
        const BATCH = 50;
        for (let i = 0; i < rows.length; i += BATCH) {
          const batch = rows.slice(i, i + BATCH);
          for (const row of batch) {
            await this.database.get('insert into', 'import_produit', {
              columns: Object.keys(row),
              data: row,
            });
          }
          this.importDone = Math.min(i + BATCH, rows.length);
        }

        this.importModal = false;
        this.resetImport();  // reset complet après import réussi
        await this.loadSessions();
        const newSession = this.sessions.find(s => s.uid === sessionUid);
        if (newSession) this.selectSession(newSession);
      } catch (e) {
        this.importError = 'Erreur import : ' + (e.message || String(e));
      } finally {
        this.importing = false;
      }
    },

    async downloadSession(session) {
      try {
        const safeUid = session.uid.replace(/'/g, "''");
        const rows = await this.database.get('select', 'import_produit', {
          select: '*',
          where: `import_uid = '${safeUid}'`,
          end: 'ORDER BY id ASC LIMIT 50000',
        });
        if (!rows || !rows.length) { alert('Aucune donnée à télécharger.'); return; }

        const ids = rows.map(r => r.id);
        const scrapingRows = await this.database.get('select', 'scraping', {
          select: '*',
          where: `id_import_produit IN (${ids.join(',')})`,
        });

        // Grouper les scrapings par id_import_produit → domaine
        const scrapingByProduit = {};
        for (const sr of scrapingRows || []) {
          const key = sr.id_import_produit;
          if (!scrapingByProduit[key]) scrapingByProduit[key] = {};
          const existing = scrapingByProduit[key][sr.domaine];
          // Prendre le plus récent, ou le validé en priorité
          if (!existing || sr.statut === 'validé') {
            scrapingByProduit[key][sr.domaine] = sr;
          }
        }

        // Collecter tous les domaines et toutes les clés résultat par domaine
        const domainsSet = new Set();
        const resultKeysByDomain = {};
        for (const sr of scrapingRows || []) {
          domainsSet.add(sr.domaine);
          if (sr.result) {
            try {
              const res = typeof sr.result === 'string' ? JSON.parse(sr.result) : sr.result;
              if (res && typeof res === 'object') {
                if (!resultKeysByDomain[sr.domaine]) resultKeysByDomain[sr.domaine] = new Set();
                Object.keys(res).forEach(k => resultKeysByDomain[sr.domaine].add(k));
              }
            } catch (e) {}
          }
        }
        const domains = [...domainsSet].sort();

        // Une ligne par produit
        const csvData = rows.map(r => {
          let dataFields = {};
          try { dataFields = typeof r.data === 'string' ? JSON.parse(r.data) : (r.data || {}); } catch (e) {}
          const line = { cle_externe: r.cle_externe || '', nom: r.nom || '', ...dataFields };

          for (const domain of domains) {
            const sr = (scrapingByProduit[r.id] || {})[domain]; // r.id = import_produit.id = id_import_produit
            const prefix = domain.replace(/\./g, '_');
            if (!sr || (this.downloadOnlyValidated && sr.statut !== 'validé')) {
              // Laisser vide
              line[`${prefix}_statut`] = '';
              for (const k of (resultKeysByDomain[domain] || [])) line[`${prefix}_${k}`] = '';
            } else {
              line[`${prefix}_statut`] = sr.statut_smd || sr.statut || '';
              if (sr.result) {
                try {
                  const res = typeof sr.result === 'string' ? JSON.parse(sr.result) : sr.result;
                  if (res && typeof res === 'object') {
                    for (const [k, v] of Object.entries(res)) line[`${prefix}_${k}`] = v;
                  }
                } catch (e) {}
              }
              for (const k of (resultKeysByDomain[domain] || [])) {
                if (!((`${prefix}_${k}`) in line)) line[`${prefix}_${k}`] = '';
              }
            }
          }
          return line;
        });

        const fields = [...new Set(csvData.flatMap(r => Object.keys(r)))];
        const csv = this.json2csv.parse(csvData, { fields, fieldNames: fields });
        const blob = new Blob([new TextEncoder().encode(csv)], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = session.nom_fichier.replace(/\.[^.]+$/, '') + '_resultats.csv';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        alert('Erreur téléchargement : ' + (e.message || String(e)));
      }
    },

    openEditSessionModal(session) {
      this.editSessionTarget = session;
      this.editSessionNom = session.nom_fichier || '';
      this.editSessionGroupe = session.groupe || '';
      this.editSessionSaving = false;
      this.editSessionModal = true;
    },

    async saveEditSession() {
      if (!this.editSessionTarget) return;
      this.editSessionSaving = true;
      try {
        const safeUid = this.editSessionTarget.uid.replace(/'/g, "''");
        const nom = this.editSessionNom.trim() || this.editSessionTarget.nom_fichier;
        const groupe = this.editSessionGroupe.trim() || null;
        await this.database.get('update', 'import_session', {
          set: `nom_fichier = "${nom.replace(/"/g, '\\"')}", groupe = ${groupe ? '"' + groupe.replace(/"/g, '\\"') + '"' : 'NULL'}`,
          where: `uid = '${safeUid}'`,
        });
        // Mettre à jour le groupe sur toutes les lignes de la session
        await this.database.get('update', 'import_produit', {
          set: `groupe = ${groupe ? '"' + groupe.replace(/"/g, '\\"') + '"' : 'NULL'}`,
          where: `import_uid = '${safeUid}'`,
        });
        this.editSessionTarget.nom_fichier = nom;
        this.editSessionTarget.groupe = groupe;
        if (this.currentSession && this.currentSession.uid === this.editSessionTarget.uid) {
          this.currentSession.nom_fichier = nom;
          this.currentSession.groupe = groupe;
        }
        this.editSessionModal = false;
      } finally {
        this.editSessionSaving = false;
      }
    },

    async deleteSession(session) {
      try {
        const safeUid = session.uid.replace(/'/g, "''");
        // Récupérer les ids des produits pour supprimer les scrapings associés
        const produits = await this.database.get('select', 'import_produit', {
          select: 'id',
          where: `import_uid = '${safeUid}'`,
        });
        if (produits && produits.length) {
          const ids = produits.map(p => p.id).join(',');
          await this.database.get('delete', 'scraping', {
            where: `id_import_produit IN (${ids})`,
          });
        }
        await this.database.get('delete', 'import_produit', {
          where: `import_uid = '${safeUid}'`,
        });
        await this.database.get('delete', 'import_session', {
          where: `uid = '${safeUid}'`,
        });
        if (this.currentSession && this.currentSession.uid === session.uid) {
          this.view = 'sessions';
          this.currentSession = null;
        }
        await this.loadSessions();
      } catch (e) {
        alert('Erreur suppression : ' + (e.message || String(e)));
      }
    },
  },
};
</script>
